class KiyohReviews extends HTMLElement {
  constructor() {
    super();
    this.template = this.querySelector('template');
    this.src = this.getAttribute('data-src');
    this.maxRetries = 3;
    this.retryDelay = 2000;
    this.reviewTemplate = this.template?.content.querySelector('[data-slide-template]')?.cloneNode(true);
  }

  connectedCallback() {
    this.loadAndRender();
  }

  get sectionRoot() {
    return document.querySelector(`#Reviews-${this.dataset.section}`);
  }

  async loadAndRender() {
    let retryCount = 0;

    const tryLoad = async () => {
      try {
        this.showLoading();
        const res = await fetch(this.src);

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const xmlText = await res.text();
        const parser = new DOMParser();
        const xml = parser.parseFromString(xmlText, 'application/xml');

        const parserError = xml.querySelector('parsererror');
        if (parserError) {
          throw new Error('XML parsing error');
        }

        const viewReviewUrl = this.#getText(xml, 'viewReviewUrl');

        const reviewNodes = Array.from(xml.querySelectorAll('reviews > reviews'));
        const reviews = reviewNodes.map((reviewEl) => {
          const author = this.#getText(reviewEl, 'reviewAuthor');
          const city = this.#getText(reviewEl, 'city');
          const date = this.#getText(reviewEl, 'dateSince')?.split('T')[0];
          const rating = this.#getText(reviewEl, 'rating');
          const contents = reviewEl.querySelectorAll('reviewContent > reviewContent');

          let oneliner = '';
          let opinion = '';

          contents.forEach((content) => {
            const group = this.#getText(content, 'questionGroup');
            const value = this.#getText(content, 'rating');

            if (group === 'DEFAULT_ONELINER') oneliner = value;
            if (group === 'DEFAULT_OPINION') opinion = value;
          });

          return { author, city, date, rating, oneliner, opinion };
        }).filter((review) => {
          const ratingOutOf10 = parseFloat(review.rating);

          if (Number.isNaN(ratingOutOf10)) return false;

          return ratingOutOf10 >= 6;
        }).slice(0, 10);

        if (reviews.length === 0) {
          throw new Error('No reviews found');
        }

        this.hideLoading();
        this.#updateCta(viewReviewUrl);
        this.#render(reviews);
      } catch (error) {
        console.error('KiyohReviews error:', error);

        if (retryCount < this.maxRetries) {
          retryCount++;
          setTimeout(tryLoad, this.retryDelay);
        } else {
          this.showError(this.getAttribute('data-error') || 'Unable to load reviews at this time');
        }
      }
    };

    await tryLoad();
  }

  #getText(context, tag) {
    return context.querySelector(tag)?.textContent.trim() || '';
  }

  #applyStars(ratingContainer, ratingOutOf10) {
    if (!ratingContainer) return;

    const ratingOutOf5 = parseFloat(ratingOutOf10) / 2.0;
    const fullStars = Math.floor(ratingOutOf5);
    const hasHalfStar = (ratingOutOf5 % 1) >= 0.4;

    const starIcons = ratingContainer.querySelectorAll('.star-icon');
    starIcons.forEach((star, index) => {
      star.classList.remove('star--full', 'star--half', 'star--empty');
      if (index < fullStars) {
        star.classList.add('star--full');
      } else if (index === fullStars && hasHalfStar) {
        star.classList.add('star--half');
      } else {
        star.classList.add('star--empty');
      }
    });
  }

  showLoading() {
    const slider = document.querySelector(`#Slider-${this.dataset.section}`);
    if (slider) {
      const loadingText = this.getAttribute('data-loading') || 'Loading reviews...';
      slider.innerHTML = `
        <div class="loading-state">
          <div class="loading-spinner"></div>
          <p>${loadingText}</p>
        </div>
      `;
    }
  }

  hideLoading() {
    const loadingState = document.querySelector(`#Slider-${this.dataset.section} .loading-state`);
    if (loadingState) {
      loadingState.remove();
    }
  }

  showError(message) {
    this.hideLoading();
    const slider = document.querySelector(`#Slider-${this.dataset.section}`);
    if (slider) {
      slider.innerHTML = `
        <div class="error-state">
          <p>${message}</p>
        </div>
      `;
    }
  }

  #updateCta(viewReviewUrl) {
    const cta = this.sectionRoot?.querySelector('[data-kiyoh-cta]');
    if (cta && viewReviewUrl) {
      cta.href = viewReviewUrl;
      cta.removeAttribute('hidden');
    }
  }

  #render(reviews) {
    const slider = document.querySelector(`#Slider-${this.dataset.section}`);
    if (!slider) {
      console.error('Slider not found:', `#Slider-${this.dataset.section}`);
      return;
    }

    if (!this.reviewTemplate) {
      console.error('KiyohReviews: Review template not found.');
      return;
    }

    slider.innerHTML = '';

    reviews.forEach((review, index) => {
      const { author, date, rating, oneliner, opinion } = review;
      const slideNode = this.reviewTemplate.cloneNode(true);

      slideNode.id = `Slide-${this.dataset.section}-${index}`;

      const authorEl = slideNode.querySelector('[data-review="author"]');
      if (authorEl) authorEl.textContent = author;

      slideNode.querySelector('[data-review="oneliner"]').textContent = oneliner;
      slideNode.querySelector('[data-review="opinion"]').textContent = opinion;
      slideNode.querySelector('[data-review="date"]').textContent = date ? new Date(date).toLocaleDateString() : '';

      const ratingContainer = slideNode.querySelector('[data-rating-container]');
      this.#applyStars(ratingContainer, rating);

      slider.appendChild(slideNode);
    });

    const sliderComponent = slider.closest('slider-component');
    if (sliderComponent) {
      sliderComponent.sliderItems = sliderComponent.querySelectorAll('[id^="Slide-"]');
      sliderComponent.initPages();
    } else if (reviews.length > 0) {
      console.error('Slider component not found');
    }
  }
}

customElements.define('kiyoh-reviews', KiyohReviews);
